<!-- eslint-disable vue/valid-v-for -->
<template>
  <div :class="['table-price', { 'table-price_no-padding': noPadding }]">
    <div
      v-for="(row, index) in getRows"
      :key="`table-price-${_uid}-row-${index}-init`"
    >
      <template v-if="index === 2 && checkOptionalMaterials">
        <div class="table-price__row table-price__row_prev_last">
          <div class="table-price__label">
            <span class="table-price__label-text">
              {{ optionalMaterials.title }}
            </span>
          </div>
        </div>

        <div
          v-for="(optRow, optRowIndex) in getOptionalRows"
          :key="`table-price-${_uid}-optionalMaterials-row-${optRowIndex}`"
          class="table-price__row table-price__row_optional-materials"
        >
          <div
            v-for="(optCol, optColIndex) in optRow"
            :key="`table-price-${_uid}-optionalMaterials-row-${optRowIndex}-${optColIndex}`"
            class="table-price__col"
          >
            <template v-if="optCol.list">
              {{ optCol.text }}
              <BaseHint v-if="optCol.hint" :hintText="optCol.hint" />
              <BaseSelect
                scrollbar="none"
                class="table-price__col-select"
                :items="Object.values(optCol.list).map(item => ({ text: item.label, value: item.id }))"
                @input="onOptionalMaterialChange($event, optCol)"
              />
            </template>
            <template v-else>{{ optCol.text }}</template>
          </div>
        </div>
      </template>

      <div
        :class="[
          'table-price__row',
          { 'table-price__row_last': index === rows.length - 1 },
          { 'table-price__hasSubrow': row.subRows },
          {
            'table-price__row_prev_last':
              index >= 2 && index !== rows.length - 1
          },
          {
            'table-price__row_smaller': index === 2
          }
        ]"
      >
        <div class="table-price__label">
          <span class="table-price__label-text">
            {{ row.label }}
          </span>
          <BaseHint v-if="row.hint" :hintText="row.hint" />
        </div>
        <div class="table-price__value">{{ row.value }}</div>
      </div>
      <div class="table-price__subRow" v-for="subRow in row.subRows">
        <div class="table-price__label">
          <span class="table-price__label-text">
            {{ subRow.label }}
          </span>
        </div>
        <div class="table-price__value">{{ subRow.value }}</div>
      </div>

      <div v-if="index === 4" class="addWorkPackageList">
        <div
          v-for="item in row.items"
          :key="`list-table-price-packages-${_uid}-row-${index}`"
        >
          <span>{{ item.label }} </span>
          <span>
            {{ item.cost }}
          </span>
        </div>
      </div>

      <div v-if="index === 4" class="addWorkPackage">
        <TableAddWorkPackage :rows="addWorkPackage" :excludePackageMessage="excludePackageMessage" />
      </div>
    </div>

    <div v-if="warning" class="table-price__warning">
      {{ warning }}
    </div>
  </div>
</template>

<script>
  import { mixin as clickaway } from 'vue-clickaway'
  import TableAddWorkPackage from './TableAddWorkPackage.vue'

  export default {
    name: 'TablePrice',
    components: {
      TableAddWorkPackage
    },
    mixins: [clickaway],
    props: {
      warning: {
        type: String,
        default: ''
      },
      optionalMaterials: {
        type: Object
      },
      materials: {
        type: Object
      },
      addWorkPackage: {
        type: Array
      },
      excludePackageMessage: {
        type: String
      },
      rows: {
        type: Array,
        required: true,
        validator (rows) {
          return rows.every(row => {
            return 'label' in row && 'value' in row
          })
        }
      },
      noPadding: {
        type: Boolean,
        default: false
      }
    },
    computed: {
      getRows () {
        if (
          (this.rows[4].items && this.rows[4].items.length) ||
          (this.addWorkPackage && this.addWorkPackage.length)) {
          return this.rows
        } else {
          return this.rows.filter((item, index) => {
            return index < 4
          })
        }
      },
      checkOptionalMaterials () {
        return !!(this.optionalMaterials && this.optionalMaterials.rows && this.optionalMaterials.rows.length)
      },
      getOptionalRows () {
        if (this.checkOptionalMaterials) {
          return this.optionalMaterials.rows.filter(item => item['col-1'].list)
        }
        return []
      }
    },
    methods: {
      onOptionalMaterialChange (event, col) {
        const index = this.materials.rows.findIndex(row => {
          return row['col-1'].position && row['col-1'].position === col.position
        })
        this.$emit('oilChange', event, index)
      }
    }
  }
</script>

<style lang="scss">
.addWorkPackageList {
  .table-price__value {
    text-wrap: nowrap;
    align-self: center;
  }

  div {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 20px;
    color: $c-gray-dark;

    span:last-child {
      min-width: 20%;
      display: inline;
      text-align: center;

      @include mobile {
        min-width: 35%;
      }
    }

    span {
      font-size: 14px;
      color: #6c6f76;

      @include mobile {
        font-size: 14px;
      }
    }

    @include tablet {
      margin-bottom: 20px;
    }

    @include mobile {
      margin-bottom: 20px;
    }
  }
}

.table-price {
  padding: 45px 40px;
  background: $c-white;

  @include mobile {
    padding: 15px 20px;
  }

  &__warning {
    margin-top: 20px;
    font-family: "ToyotaType-Book", Arial, Helvetica, sans-serif;
    font-size: 13px;
    line-height: 18px;
    color: $c-input-grey;
  }

  &__label {
    position: relative;
    display: flex;
    align-items: center;
    width: 80%;

    &-text {
      color: $c-gray-dark;

      @include mobile {
        font-size: 14px;
      }
    }
  }

  &__subRow {
    padding-left: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
    color: $c-black;

    .table-price__label-text {
      font-weight: 400;
      color: $c-black;
      //font-size: 14px;
    }

    .table-price__value {
      font-weight: 400;
      //font-size: 14px;
    }

    &:last-of-type {
      margin-bottom: 16px;
    }
  }

  &__value {
    width: 20%;
    color: inherit;
    text-align: right;
    margin-right: 28px;

    @include mobile {
      width: 35%;
      font-size: 14px;
      margin-right: 25px;
    }

    @include mobileXs {
      width: 40%;
      font-size: 14px;
    }
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 30px;
    color: $c-gray-dark;
    font-weight: 900;

    @include mobile {
      margin-bottom: 15px;
    }

    &_prev_last {
      position: relative;
      // align-items: flex-start;
      padding-top: 24px;
      margin-bottom: 24px;
      color: $c-black;

      @include mobile {
        padding-top: 15px;
      }

      &::before {
        position: absolute;
        top: 0;
        left: -40px;
        width: calc(100% + 80px);
        height: 1px;
        content: "";
        background: $c-gray;

        @include mobile {
          left: -20px;
          width: calc(100% + 40px);
        }
      }

      &:not(.table-price__row_smaller) {
        .table-price__label {
          &-text {
            font-size: 18px;
            color: $c-black;

            @include mobile {
              font-size: 16px;
            }
          }
        }

        .table-price__value {
          font-size: 18px;

          @include mobile {
            font-size: 16px;
          }
        }
      }
    }

    &_last {
      position: relative;
      // align-items: flex-start;
      padding: 30px 0;
      margin-bottom: 0;
      font-weight: 900;
      color: $c-black;

      @include mobile {
        padding: 15px 0;
      }

      &::after {
        position: absolute;
        bottom: 0;
        left: -40px;
        width: calc(100% + 80px);
        height: 1px;
        content: "";
        background: $c-gray;

        @include mobile {
          left: -20px;
          width: calc(100% + 40px);
        }
      }

      &::before {
        position: absolute;
        top: 0;
        left: -40px;
        width: calc(100% + 80px);
        height: 1px;
        content: "";
        background: $c-gray;

        @include mobile {
          left: -20px;
          width: calc(100% + 40px);
        }
      }

      &:not(.table-price__row_smaller) {
        .table-price__label {
          &-text {
            font-size: 18px;
            color: $c-black;

            @include mobile {
              font-size: 16px;
            }
          }
        }

        .table-price__value {
          font-size: 18px;

          @include mobile {
            font-size: 16px;
          }
        }
      }
    }

    &_optional-materials {
      justify-content: space-between;
      column-gap: 24px;
      padding-right: 28px;

      @include mobile {
        row-gap: 12px;
        justify-content: flex-end;
        flex-wrap: wrap;
        padding-right: 25px;
      }
    }

    &.table-price__hasSubrow {
      margin-bottom: 12px;
    }
  }

  &__col {
    display: flex;
    align-items: center;
    justify-content: space-between;
    column-gap: 24px;
    flex-shrink: 0;
    min-width: 56px;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    max-width: 400px;

    &:first-child {
      flex-grow: 1;

      @include mobile {
        width: 100%;
        column-gap: 12px;
      }
    }

    &:last-child {
      min-width: 84px;
      justify-content: flex-end;
    }

    &-select {
      min-width: 200px;
      padding-bottom: 0;
      margin-right: 24px;
      font-weight: 400;

      @include mobile {
        width: 50%;
        min-width: 175px;
        margin-right: 0;
      }

      .select__arrow {
        right: 16px;
        top: 16px;
        width: 16px;
        height: 16px;
      }

      .select__dropdown {
        top: 100%;
      }
    }
  }

  &_no-padding {
    padding: 0;
    overflow: hidden;

    .table-price__row:last-child::before {
      left: 0;
      width: 100%;
    }
  }
}
</style>
